import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import './UserEntityRequests.css';

const UserEntityRequests = ({ user }) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUserRequests();
  }, []);

  const loadUserRequests = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/entity-join/my-requests');
      if (response.data.success) {
        setRequests(response.data.requests);
      }
    } catch (error) {
      setError(t('userEntityRequests.errorLoadingRequests'));
      console.error('Erreur chargement demandes utilisateur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm(t('userEntityRequests.cancelConfirmation'))) {
      return;
    }

    try {
      const response = await apiClient.delete(`/api/entity-join/cancel/${requestId}`);
      if (response.data.success) {
        await loadUserRequests(); // Recharger la liste
      }
    } catch (error) {
      setError(error.response?.data?.message || t('userEntityRequests.cancelError'));
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': t('userEntityRequests.pending'),
      'approved': t('userEntityRequests.approved'),
      'rejected': t('userEntityRequests.rejected')
    };
    return labels[status] || status;
  };

  const getStatusClass = (status) => {
    return `status-${status}`;
  };

  const getRequestTypeLabel = (type) => {
    return type === 'join_existing' ? t('userEntityRequests.joinOrganization') : t('userEntityRequests.createOrganization');
  };

  const getRoleLabel = (role) => {
    const roles = {
      'user': t('userEntityRequests.user'),
      'analyst': t('userEntityRequests.analyst'),
      'org-admin': t('userEntityRequests.administrator')
    };
    return roles[role] || role;
  };

  if (loading) {
    return <div className="loading">{t('userEntityRequests.loadingRequests')}</div>;
  }

  return (
    <div className="user-entity-requests">
      <h3>{t('userEntityRequests.title')}</h3>
      
      {error && <div className="alert alert-error">{error}</div>}

      {requests.length === 0 ? (
        <div className="no-requests">
          <p>{t('userEntityRequests.noRequests')}</p>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map(request => (
            <div key={request._id} className="request-card">
              <div className="request-header">
                <h4>{getRequestTypeLabel(request.requestType)}</h4>
                <span className={`status-badge ${getStatusClass(request.status)}`}>
                  {getStatusLabel(request.status)}
                </span>
              </div>

              <div className="request-details">
                {request.requestType === 'join_existing' ? (
                  <>
                    <div className="detail-row">
                      <strong>{t('userEntityRequests.organization')}:</strong> 
                      {request.organizationId ? request.organizationId.name : t('userEntityRequests.deletedOrganization')}
                    </div>
                    <div className="detail-row">
                      <strong>{t('userEntityRequests.requestedRole')}:</strong> 
                      {getRoleLabel(request.requestedRole)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="detail-row">
                      <strong>{t('userEntityRequests.organizationName')}:</strong> 
                      {request.organizationName}
                    </div>
                    {request.organizationDescription && (
                      <div className="detail-row">
                        <strong>{t('userEntityRequests.description')}:</strong> 
                        {request.organizationDescription}
                      </div>
                    )}
                  </>
                )}

                <div className="detail-row">
                  <strong>{t('userEntityRequests.requestDate')}:</strong> 
                  {new Date(request.createdAt).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>

                {request.validatedAt && (
                  <div className="detail-row">
                    <strong>{t('userEntityRequests.validationDate')}:</strong> 
                    {new Date(request.validatedAt).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                )}

                {request.validationComment && (
                  <div className="detail-row comment">
                    <strong>{t('userEntityRequests.comment')}:</strong> 
                    <p>{request.validationComment}</p>
                  </div>
                )}
              </div>

              <div className="request-actions">
                {request.status === 'pending' && (
                  <button
                    className="btn btn-danger"
                    onClick={() => handleCancelRequest(request._id)}
                  >
                    Annuler la demande
                  </button>
                )}

                {request.status === 'approved' && request.requestType === 'create_organization' && (
                  <div className="success-message">
                    ✅ Votre organisation a été créée avec succès ! Vous êtes maintenant administrateur.
                  </div>
                )}

                {request.status === 'approved' && request.requestType === 'join_existing' && (
                  <div className="success-message">
                    ✅ Vous avez été ajouté à l'organisation avec succès !
                  </div>
                )}

                {request.status === 'rejected' && (
                  <div className="rejected-message">
                    ❌ Votre demande a été rejetée.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserEntityRequests;
